import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ClientDetailsFields } from '@components/checkout/ClientDetailsFields';
import { LegalAcceptanceFields } from '@components/checkout/LegalAcceptanceFields';
import { Button } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { EditorialHeader, Overline, PageSection } from '@components/common/Editorial';
import { usePageTitle } from '@hooks/usePageTitle';
import { useRobotsDirective } from '@hooks/useRobotsDirective';
import { submitStandaloneConsent } from '@services/consent/standaloneConsent';
import { validateClientDetails } from '@services/checkout/checkoutIntent';
import { createRequestId, focusFirstInvalid } from '@services/checkout/requestId';
export const ConsentPage = () => {
    const { t, i18n } = useTranslation();
    const [client, setClient] = useState({ fullName: '', email: '', phone: '' });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [immediateServiceAccepted, setImmediateServiceAccepted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deliveryError, setDeliveryError] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const requestId = useRef(createRequestId());
    const submissionLock = useRef(false);
    const formRef = useRef(null);
    usePageTitle('consent.title');
    useRobotsDirective('noindex,nofollow');
    const clientErrors = validateClientDetails(client);
    const termsError = !termsAccepted;
    const immediateServiceError = !immediateServiceAccepted;
    const hasErrors = Object.values(clientErrors).some(Boolean) || termsError || immediateServiceError;
    const submit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        setDeliveryError(false);
        if (hasErrors) {
            focusFirstInvalid(formRef.current);
            return;
        }
        if (submissionLock.current)
            return;
        submissionLock.current = true;
        setSubmitting(true);
        try {
            const result = await submitStandaloneConsent({
                requestId: requestId.current,
                client: {
                    fullName: client.fullName.trim(),
                    email: client.email.trim(),
                    phone: client.phone.trim()
                },
                acceptance: { termsAccepted, immediateServiceAccepted },
                locale: i18n.resolvedLanguage ?? i18n.language
            });
            setConfirmation(result);
            window.requestAnimationFrame(() => document.getElementById('consent-confirmation')?.focus());
        }
        catch {
            setDeliveryError(true);
        }
        finally {
            submissionLock.current = false;
            setSubmitting(false);
        }
    };
    const formattedDate = confirmation
        ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
            dateStyle: 'long',
            timeStyle: 'short',
            timeZone: 'Europe/Madrid'
        }).format(new Date(confirmation.acceptedAt))
        : '';
    return (_jsxs(_Fragment, { children: [_jsx(EditorialHeader, { eyebrow: t('consent.eyebrow'), title: t('consent.title'), intro: t('consent.intro') }), _jsx(PageSection, { children: _jsx(ConsentContainer, { children: confirmation ? (_jsxs(Confirmation, { id: "consent-confirmation", tabIndex: -1, role: "status", children: [_jsx(CheckCircle2, { size: 34, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx(Overline, { children: t('consent.successEyebrow') }), _jsx("h2", { children: t('consent.successTitle') }), _jsx("p", { children: t('consent.successText') }), _jsxs(Details, { children: [_jsxs("div", { children: [_jsx("span", { children: t('consent.referenceLabel') }), _jsx("strong", { children: confirmation.consentReference })] }), _jsxs("div", { children: [_jsx("span", { children: t('consent.dateLabel') }), _jsx("strong", { children: formattedDate })] })] }), _jsx(Notice, { children: t('consent.noPayment') }), _jsx("p", { children: t('consent.nextStep') })] })] })) : (_jsxs("form", { ref: formRef, onSubmit: submit, noValidate: true, children: [_jsx(Overline, { children: t('checkout.acceptancesTitle') }), _jsx(ClientDetailsFields, { idPrefix: "consent", values: client, errors: clientErrors, submitted: submitted, onChange: (field, value) => setClient((current) => ({ ...current, [field]: value })) }), _jsxs(PrivacyInfo, { children: [_jsx(ShieldCheck, { size: 22, "aria-hidden": "true" }), _jsx("p", { children: _jsx(Trans, { i18nKey: "checkout.privacyInfo", components: { privacy: _jsx(Link, { to: "/privacy" }) } }) })] }), _jsx(LegalAcceptanceFields, { idPrefix: "consent", secondaryKind: "service", termsAccepted: termsAccepted, secondaryAccepted: immediateServiceAccepted, submitted: submitted, termsError: termsError, secondaryError: immediateServiceError, onTermsChange: setTermsAccepted, onSecondaryChange: setImmediateServiceAccepted }), _jsx(SubmitButton, { type: "submit", disabled: submitting, children: t(submitting ? 'consent.submitting' : 'consent.submit') }), submitted && hasErrors && _jsx(Error, { role: "alert", children: t('checkout.formError') }), deliveryError && _jsx(Error, { role: "alert", children: t('consent.deliveryError') })] })) }) })] }));
};
const ConsentContainer = styled(Container) `
  max-width: 780px;
`;
const PrivacyInfo = styled.div `
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.8rem;
  margin: 1rem 0 1.5rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    line-height: 1.65;
  }
  a {
    color: ${({ theme }) => theme.colors.navy900};
    text-decoration: underline;
  }
`;
const SubmitButton = styled(Button) `
  width: 100%;
  margin-top: 1.5rem;
  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;
const Error = styled.p `
  margin: 0.65rem 0 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  line-height: 1.55;
`;
const Confirmation = styled.section `
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  border-top: 3px solid ${({ theme }) => theme.colors.cyan500};
  padding: clamp(1.5rem, 4vw, 2.5rem);
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.navy900};
  > svg {
    color: ${({ theme }) => theme.colors.blue600};
  }
  h2 {
    margin: 0.75rem 0;
    font-size: clamp(1.5rem, 4vw, 2rem);
  }
  p {
    color: ${({ theme }) => theme.colors.muted};
    line-height: 1.7;
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;
const Details = styled.div `
  display: grid;
  gap: 1rem;
  margin: 1.5rem 0;
  div {
    display: grid;
    gap: 0.25rem;
  }
  span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.8125rem;
  }
  strong {
    overflow-wrap: anywhere;
  }
`;
const Notice = styled.p `
  border-left: 3px solid ${({ theme }) => theme.colors.cyan500};
  padding-left: 1rem;
  color: ${({ theme }) => theme.colors.ink} !important;
  font-weight: 700;
`;
